// src/hooks/useAgentChatSession.ts
// Чат ИИ-агента (FR-4): реальный бэкенд (chatApi + стриминг), как у свободного
// чата, но с собственным chat_id (demo_agent_chat_id) и невидимым контекстом
// «рабочей папки» (wrapAgentMessage). Историю показываем без служебных хвостов.
import { useState, useCallback, useEffect, useRef } from 'react';
import type { ChatMsg } from 'types';
import type { DamFile } from 'mocks/dam';
import { useAuthStore } from 'store/authStore';
import { fetchDefaultProjectId } from 'services/projectsApi';
import {
  createChat,
  getChatMessages,
  resolveChatRoleId,
  sendMessage as sendMessageApi,
  stopChat as stopChatApi,
} from 'services/chatApi';
import { useStreamingMessage } from 'hooks/useStreamingMessage';
import { useObfuscationGate } from 'hooks/useObfuscationGate';
import { createDamFile, findCreatedFileSync } from 'services/demoApi/damApi';
import {
  CREATE_FILE_OPEN,
  parseCreateFileMarker,
  stripCreateFileMarker,
} from 'services/demoApi/agentFileMarker';
import { wrapAgentMessage, type AgentContextFile } from 'services/demoApi/promptDecorators';

export const AGENT_CHAT_TASK_ID_KEY = 'agent_chat_task_id';
const AGENT_CHAT_ID_KEY = 'demo_agent_chat_id';

/** Файл, прикреплённый к сообщению агента (FR-3.2) */
export interface AgentAttachment {
  /** null — файл не найден в хранилище (удалён пользователем) */
  fileId: string | null;
  name: string;
  ext: DamFile['ext'];
}

export type AgentChatMsg = ChatMsg & { attachedFile?: AgentAttachment };

export interface UseAgentChatSessionResult {
  messages: AgentChatMsg[];
  displayedContent: string;
  isLoading: boolean;
  /** Идёт имитация обфускации данных перед внешней моделью (вывод ответа отложен) */
  obfuscating: boolean;
  error: string | null;
  /** Последний созданный агентом файл — для подсветки в панели (FR-3.2) */
  createdFile: DamFile | null;
  sendMessage: (
    text: string,
    folder: { id: string; name: string },
    files: AgentContextFile[]
  ) => Promise<void>;
  /** Прервать текущую генерацию ответа агента */
  stopGeneration: () => Promise<void>;
  startNewDialog: () => void;
}

export function useAgentChatSession(): UseAgentChatSessionResult {
  const user = useAuthStore((s) => s.user);
  const projectId = user?.projectId ?? '';
  const chatRoleId = resolveChatRoleId(user?.roleId, user?.role);
  const [messages, setMessages] = useState<AgentChatMsg[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdFile, setCreatedFile] = useState<DamFile | null>(null);
  const chatIdRef = useRef<string | null>(localStorage.getItem(AGENT_CHAT_ID_KEY));
  const sendFolderRef = useRef<{ id: string; name: string } | null>(null);
  const processedMarkerRef = useRef<Set<string>>(new Set());

  // Восстановить историю диалога агента после перезагрузки; у AI-сообщений
  // срезаем маркер (FR-3.3), а вложение восстанавливаем из маркера + хранилища
  useEffect(() => {
    const chatId = chatIdRef.current;
    if (!chatId) return;
    getChatMessages(chatId)
      .then((history) => {
        if (history.length === 0) return;
        setMessages(
          history.map((m): AgentChatMsg => {
            if (m.role !== 'AI') return m;
            const { command, cleanText } = parseCreateFileMarker(m.content);
            if (!command) return { ...m, content: cleanText };
            // Файл был создан в прошлой сессии — ищем его в localStorage;
            // null (удалён) тоже показываем: карточка без скачивания
            const stored = findCreatedFileSync(command.name, command.ext);
            return {
              ...m,
              content: stripCreateFileMarker(cleanText),
              attachedFile: {
                fileId: stored?.id ?? null,
                name: stored?.name ?? command.name,
                ext: command.ext,
              },
            };
          })
        );
      })
      .catch(() => {
        // Чат мог быть удалён на бэке — начнём новый при первом сообщении
        chatIdRef.current = null;
        localStorage.removeItem(AGENT_CHAT_ID_KEY);
      });
  }, []);

  // Для внешней модели сначала «обфускация данных» (7–10 c) — на это время набор
  // ответа поставлен на паузу, чтобы текст печатался посимвольно ПОСЛЕ обфускации.
  const { obfuscating } = useObfuscationGate(isLoading);

  const { displayedContent, resetStreaming } = useStreamingMessage(
    setMessages,
    setIsLoading,
    AGENT_CHAT_TASK_ID_KEY,
    obfuscating
  );

  // FR-3: финальный ответ агента может содержать блок CREATE_FILE —
  // создаём файл в рабочей папке и заменяем сообщение на чистый текст.
  // Дополнительная защита: если после первого parseCreateFileMarker cleanText
  // всё ещё содержит CREATE_FILE_OPEN (модель выдала два блока) — прогоняем
  // stripCreateFileMarker повторно (максимум 3 итерации).
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'AI' || !last.content.includes(CREATE_FILE_OPEN)) return;
    const key = `${messages.length}:${last.created_at ?? ''}`;
    if (processedMarkerRef.current.has(key)) return; // защита от двойного эффекта
    processedMarkerRef.current.add(key);

    const { command, cleanText: firstClean } = parseCreateFileMarker(last.content);

    // Дополнительная защита от двойного блока (до 3 итераций)
    let cleanText = firstClean;
    let iterations = 0;
    while (cleanText.includes(CREATE_FILE_OPEN) && iterations < 3) {
      cleanText = stripCreateFileMarker(cleanText);
      iterations += 1;
    }

    // Сначала создаём файл, чтобы прикрепить его к сообщению вместе с чистым текстом
    let attachedFile: AgentAttachment | undefined;
    if (command && sendFolderRef.current) {
      const file = createDamFile({
        folderId: sendFolderRef.current.id,
        name: command.name,
        ext: command.ext,
        content: command.content,
      });
      setCreatedFile(file);
      attachedFile = { fileId: file.id, name: file.name, ext: file.ext };
    }
    setMessages((prev) =>
      prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: cleanText, attachedFile } : m))
    );
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveProjectId = useCallback(async (): Promise<string> => {
    if (projectId) return projectId;
    const fetched = await fetchDefaultProjectId();
    if (!fetched) return '';
    useAuthStore.setState((state) => ({
      user: state.user ? { ...state.user, projectId: fetched } : null,
    }));
    return fetched;
  }, [projectId]);

  const ensureChatId = useCallback(async (): Promise<string | null> => {
    if (chatIdRef.current) return chatIdRef.current;
    const activeProjectId = await resolveProjectId();
    if (!activeProjectId) return null;
    const chat = await createChat(activeProjectId, undefined, 'Диалог с агентом');
    if (!chat?.id) return null;
    chatIdRef.current = chat.id;
    localStorage.setItem(AGENT_CHAT_ID_KEY, chat.id);
    return chat.id;
  }, [resolveProjectId]);

  const sendMessage = useCallback(
    async (text: string, folder: { id: string; name: string }, files: AgentContextFile[]) => {
      if (!text.trim() || isLoading) return;
      sendFolderRef.current = folder;
      setError(null);
      setIsLoading(true);
      resetStreaming();

      setMessages((prev) => [
        ...prev,
        { role: 'User', content: text.trim(), created_at: new Date().toISOString() },
      ]);

      try {
        const activeProjectId = await resolveProjectId();
        if (!activeProjectId) {
          setIsLoading(false);
          setError(
            'Не удалось определить проект для чата. Перезайдите в систему или обратитесь к администратору.'
          );
          return;
        }

        const chatId = await ensureChatId();
        if (!chatId) {
          setIsLoading(false);
          setError('Не удалось создать чат. Проверьте подключение и попробуйте ещё раз.');
          return;
        }

        const response = await sendMessageApi(
          chatId,
          {
            human_message: wrapAgentMessage(text.trim(), folder.name, files),
            context_messages_limit: null,
            // Глубокий анализ документов недоступен в чате агента — всегда выключен
            analysys_mode: false,
          },
          chatRoleId
        );
        if (!response?.task_id) {
          setIsLoading(false);
          setError('Сервер не запустил генерацию ответа. Попробуйте ещё раз.');
          return;
        }
        localStorage.setItem(AGENT_CHAT_TASK_ID_KEY, response.task_id);
        localStorage.setItem(
          `stream_chat_${response.task_id}`,
          JSON.stringify({ role: 'AI', content: '', isStreaming: true })
        );
      } catch {
        setIsLoading(false);
        setError(
          'Не удалось отправить сообщение агенту. Проверьте подключение и попробуйте ещё раз.'
        );
      }
    },
    [isLoading, resetStreaming, resolveProjectId, ensureChatId, chatRoleId]
  );

  const stopGeneration = useCallback(async () => {
    // task_id читаем ДО resetStreaming — он стирает ключ из localStorage
    const taskId = localStorage.getItem(AGENT_CHAT_TASK_ID_KEY);
    const currentChatId = chatIdRef.current;

    resetStreaming();
    setIsLoading(false);

    if (currentChatId && taskId) {
      try {
        await stopChatApi(currentChatId, taskId);
      } catch {
        // non-critical: бэкенд мог уже завершить задачу
      }
    }
  }, [resetStreaming]);

  const startNewDialog = useCallback(() => {
    localStorage.removeItem(AGENT_CHAT_ID_KEY);
    localStorage.removeItem(AGENT_CHAT_TASK_ID_KEY);
    chatIdRef.current = null;
    resetStreaming();
    setMessages([]);
    setError(null);
    setIsLoading(false);
    setCreatedFile(null);
    processedMarkerRef.current.clear();
    sendFolderRef.current = null;
  }, [resetStreaming]);

  return {
    messages,
    displayedContent,
    isLoading,
    obfuscating,
    error,
    createdFile,
    sendMessage,
    stopGeneration,
    startNewDialog,
  };
}
